<template>
  <div v-if="hasUsageData" class="usage" :class="[`usage--${mode}`, `usage--${state}`]">
    <div class="usage__main">
      <div class="usage__top">
        <p class="usage__title">
          <span class="usage__used">{{ formatNumber(used) }}</span>
          <span class="usage__divider">/</span>
          <span class="usage__limit">{{ includedLabel }}</span>
          <span class="usage__caption-inline">API calls consumidas</span>
          <a-tooltip v-if="!isPostpaid" :title="PREPAID_TOOLTIP">
            <InfoCircleOutlined class="usage__info" />
          </a-tooltip>
          <a-tooltip v-else>
            <template #title>
              <div>API calls gratuitas: {{ formatNumber(freeCalls) }}</div>
              <div>API calls de cobro: {{ overageCallsLabel }}</div>
            </template>
            <InfoCircleOutlined class="usage__info" />
          </a-tooltip>
        </p>

        <div v-if="isPostpaid" class="usage__side">
          <p v-if="showLimitIndicator" class="usage__amount">
            <BellOutlined />
            <span>Límite de consumo: {{ formatMoney(consumedAmount) }} de {{ formatMoney(limitAmount) }}</span>
            <a-popover trigger="hover" placement="bottom">
              <template #content>
                <div class="usage__popover">
                  <p>
                    Al agotar tus API calls gratuitas, se te facturará {{ pricePhrase }} por cada una.
                    Al alcanzar <strong>{{ formatMoney(limitAmount) }}</strong>, se te notificará y
                    <strong>tu consumo se detendrá</strong>.
                  </p>
                  <a-button type="link" class="usage__popover-link" @click="showLimitModal = true">
                    Configurar límite de consumo
                  </a-button>
                </div>
              </template>
              <InfoCircleOutlined class="usage__info" />
            </a-popover>
          </p>

          <p v-else-if="mode === 'continue' && shownNotificationAmount !== null" class="usage__amount">
            <span>Monto facturado: {{ formatMoney(billedAmount) }}</span>
            <span class="usage__notify">/ Notificar en {{ formatMoney(shownNotificationAmount) }}</span>
            <a-popover trigger="hover" placement="bottom">
              <template #content>
                <div class="usage__popover">
                  <p>
                    Al agotar tus API calls gratuitas, se te facturará {{ pricePhrase }} por cada una.
                    Al acumular <strong>{{ formatMoney(shownNotificationAmount) }}</strong>, se te notificará y
                    <strong>tu consumo continuará</strong>.
                  </p>
                  <a-button type="link" class="usage__popover-link" @click="showLimitModal = true">
                    Configurar límite de consumo
                  </a-button>
                </div>
              </template>
              <InfoCircleOutlined class="usage__info" />
            </a-popover>
          </p>
        </div>
      </div>

      <a-progress
        :percent="consumedPercent"
        :show-info="false"
        :stroke-color="progressColor"
        size="small"
      />

      <p class="usage__caption">
        <template v-if="isPostpaid">
          Se cobrará el consumo de cada API call al agotarse las
          {{ formatNumber(freeCalls) }} gratuitas.
        </template>
        <template v-else>
          Al finalizar el ciclo de facturación, se reiniciarán las
          {{ formatNumber(freeCalls) }} API calls gratuitas.
        </template>
      </p>

      <p v-if="state !== ApiCloudUsageState.Ok" class="usage__message">
        <WarningOutlined v-if="state === ApiCloudUsageState.Low" class="usage__icon" />
        <CloseCircleOutlined v-else-if="state === ApiCloudUsageState.Exhausted" class="usage__icon" />
        <span>{{ usageMessage }}</span>
      </p>

      <a-alert
        v-if="isPostpaid && hasPendingLimitChange"
        class="usage__pending"
        type="warning"
        show-icon
        message="Este nuevo límite se aplicará al inicio del próximo ciclo de facturación."
      />
    </div>

    <div class="usage__actions">
      <a-button v-if="isPostpaid" @click="showLimitModal = true">
        <template #icon><SettingOutlined /></template>
        Configurar límite
      </a-button>
      <a-button @click="goToConsumption">
        <template #icon><DollarCircleOutlined /></template>
        {{ isPostpaid ? "Ver consumos" : "Ver consumos de API calls" }}
      </a-button>
    </div>

    <ConfigureApiCloudLimitModal
      v-if="isPostpaid"
      v-model:open="showLimitModal"
      :price="price"
      :billed-amount="billedAmount"
      :initial-amount="limitAmount"
      :initial-mode="mode"
      @save="handleSaveLimit"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import SlotManager from "@/app/settings/services/slots/SlotManager";
import { useRouter } from "vue-router";
import {
  BellOutlined,
  CloseCircleOutlined,
  DollarCircleOutlined,
  InfoCircleOutlined,
  SettingOutlined,
  WarningOutlined,
} from "@ant-design/icons-vue";

import ConfigureApiCloudLimitModal from "@/app/platformSettings/components/template/ConfigureApiCloudLimitModal.vue";
import { apiService } from "@/app/platformSettings/services/ApiService";
import type { ApiCloudLimit, ApiCloudLimitMode } from "@/@types/platformSettings/apiCloudLimit";
import {
  ApiCloudUsageState,
  useApiCloudUsage,
} from "@/app/shared/composables/useApiCloudUsage";

const props = withDefaults(
  defineProps<{
    // Permite a un padre forzar el modo cuando el backend exponga el campo explícito.
    mode?: ApiCloudLimitMode | null;
    // Monto (USD) al que se notifica en modo "continue". Hoy ningún llamador lo provee.
    notificationAmount?: number | null;
  }>(),
  {
    mode: null,
    notificationAmount: null,
  }
);

const router = useRouter();

const PREPAID_TOOLTIP =
  "Cantidad de API calls gratuitas por ciclo de facturación. Las que no se utilicen se descartarán al finalizar el ciclo y la cantidad se reiniciará.";

// Respuesta del endpoint de límite. `null` significa "el endpoint todavía no respondió" y
// activa los respaldos locales (marcados como tales).
const limit = ref<ApiCloudLimit | null>(null);

const fetchLimit = async () => {
  try {
    limit.value = await apiService.getApiCloudLimit();
  } catch (error) {
    console.error("No se pudo obtener el límite de consumo de API calls", error);
  }
};

// Enum del backend (`helpers/choices.py`): 1 = Prepago, 2 = Postpago.
const API_CLOUD_PRE_PAYMENT_BILLING_TYPE = 1;

// Prepago/postpago. Fuente primaria: `is_pre_payment` que ya viaja en `charges/api-cloud/`; si no
// está, el `api_cloud_type_billing` del propio endpoint de API Cloud. Ya no se usa
// `SlotManager.getInfoSlots().slot_type_billing` salvo como respaldo (ver más abajo).
const apiPrePayment = computed<boolean | null>(() => {
  const row = usages.value[0];
  if (row && typeof row.is_pre_payment === "boolean") return row.is_pre_payment;

  const billingType = limit.value?.api_cloud_type_billing;
  if (typeof billingType === "number") return billingType === API_CLOUD_PRE_PAYMENT_BILLING_TYPE;

  return null;
});

// Respaldo legado: solo se consulta si el campo propio de API Cloud está genuinamente ausente en
// ambas respuestas (`apiPrePayment` queda `null`).
const slotsPrePayment = ref<boolean | null>(null);
const loadBillingTypeFallback = async () => {
  try {
    const slots = await new SlotManager().getInfoSlots();
    slotsPrePayment.value = slots?.slot_type_billing !== "Postpago";
  } catch (error) {
    slotsPrePayment.value = null;
  }
};

const isPostpaid = computed(() => {
  if (apiPrePayment.value !== null) return !apiPrePayment.value;
  return slotsPrePayment.value === false;
});

const {
  fetchUsages,
  usages,
  included,
  used,
  available,
  consumedPercent: rawPercent,
  state,
} = useApiCloudUsage();

// Respaldo del tramo gratuito base. El valor real llega en `free_calls` de cada fila de
// `GET /billing/charges/api-cloud/`, tomado de `configuration.api_cloud_calls.value` del plan;
// esta constante solo aplica cuando el endpoint no responde o el campo viene ausente.
const FALLBACK_FREE_API_CALLS = 10000;

// Visibilidad. Prepago conserva el comportamiento previo: sin `included_calls` no hay tarjeta.
// Postpago debe renderizar cuando responda el consumo **o** el límite, para que el modo
// "continue" (`included_calls` `null`) no desaparezca al recargar cuando el endpoint ya respondió.
const hasUsageData = computed(() => {
  if (!isPostpaid.value) return available.value !== null;
  return available.value !== null || limit.value !== null;
});

const formatNumber = (value: number | null) =>
  value === null ? "" : value.toLocaleString("en-US");

const formatMoney = (value: number | null) =>
  value === null ? "" : `$${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

// El precio se renderiza desde `overage_price`; nunca se usa un literal en el componente.
const formatPrice = (value: number | null) =>
  value === null ? "" : `$${value.toLocaleString("en-US", { maximumFractionDigits: 6 })}`;

const consumedPercent = computed(() => {
  if (!used.value) return 0;
  // Si hay consumo pero el porcentaje redondeado da 0, aseguramos un mínimo de 1% para que sea visible
  return Math.max(1, rawPercent.value);
});

// Presentación por estado (preexistente, restaurada de forma aditiva). El estado se calcula en
// `useApiCloudUsage`; el umbral de aviso no se duplica ni se hardcodea en esta barra (F5).
const progressColor = computed(
  () =>
    ({
      [ApiCloudUsageState.Ok]: "#52c41a",
      [ApiCloudUsageState.Low]: "#faad14",
      [ApiCloudUsageState.Exhausted]: "#f5222d",
    })[state.value]
);

// Mensaje por estado (copy preexistente del componente compartido).
const usageMessage = computed(() => {
  if (state.value === ApiCloudUsageState.Exhausted)
    return "Acabaste tus API calls. El servicio se detuvo y reiniciará al finalizar tu período de facturación.";
  if (state.value === ApiCloudUsageState.Low)
    return "Quedan menos de 2,000 API calls. Si se acaban, el servicio será detenido.";
  return `Tus ${formatNumber(included.value)} API calls se reiniciarán al finalizar tu actual período de facturación.`;
});

// Último respaldo del modo, solo para cuando el endpoint no ha respondido: `included_calls`
// `null` => "continue" (sin límite de API calls), número => "stop" (con límite).
// La fuente real es `mode` del endpoint `GET /api/v1/api-cloud/limit/`.
const derivedMode = (includedCalls: number | null): ApiCloudLimitMode =>
  includedCalls === null ? "continue" : "stop";

const mode = computed<ApiCloudLimitMode>(
  () => limit.value?.mode ?? props.mode ?? derivedMode(included.value)
);

const includedLabel = computed(() => {
  if (mode.value === "continue") return "Ilimitado";
  // `included_calls` del API ya es el total (tramo gratuito + tramo del monto configurado).
  return formatNumber(included.value);
});

const currentRow = computed(() => usages.value[0]);

// Tramo gratuito base del plan, tal como lo publica el endpoint. Sin dato válido se usa el
// respaldo, para no dejar la barra con `NaN` ni con un cero que rompa el cálculo de consumo.
const freeCalls = computed(() => {
  const raw = Number(currentRow.value?.free_calls);
  return Number.isFinite(raw) && raw > 0 ? raw : FALLBACK_FREE_API_CALLS;
});

const price = computed(() => {
  const raw = currentRow.value?.overage_price;
  if (raw === undefined || raw === null || raw === "") return null;

  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
});

const priceText = computed(() => formatPrice(price.value));

// Monto del límite EN VIGOR. `amount_usd` ya es el monto que rige ahora: si un cambio quedó
// diferido, el endpoint sigue devolviendo el previo y publica el nuevo en `pending_amount_usd`,
// así que el número en pantalla **no baja** antes de que el cambio entre en vigencia.
const limitAmount = computed(() => limit.value?.amount_usd ?? null);

// API calls de cobro = calls cubiertas por el monto **en vigor** (monto / precio). Ya no se
// deriva de `included_calls − free_calls`; esa aritmética desapareció con el endpoint.
const billableCalls = computed(() => {
  if (limitAmount.value === null || !price.value) return null;
  return Math.round(limitAmount.value / price.value);
});

const overageCallsLabel = computed(() =>
  mode.value === "continue" ? "Ilimitado" : formatNumber(billableCalls.value)
);

// CONSUMO / FACTURADO — se mide desde el TRAMO GRATUITO. Distinción: `overage_calls` y
// `total_price` de `charges/api-cloud/` son el EXCEDENTE SOBRE EL LÍMITE EFECTIVO (uso de
// facturación cuando hay autorización de continuación). El monto consumido/facturado, en cambio,
// usa la base gratuita (`max(0, calls_count − free_calls) × precio`), la misma que el
// `consumed_usd` del endpoint, que es la autoridad. La derivación local es solo el respaldo para
// cuando el endpoint no ha respondido.
const fallbackConsumedAmount = computed(() =>
  price.value === null ? null : Math.max(0, used.value - freeCalls.value) * price.value
);

const consumedAmount = computed(() =>
  limit.value?.consumed_usd ?? fallbackConsumedAmount.value
);

// En modo continuar el diseño llama "Monto facturado" a este mismo consumo medido desde el tramo
// gratuito; nunca a `total_price` (excedente sobre el límite efectivo).
const billedAmount = computed(() => consumedAmount.value);

const shownNotificationAmount = computed(() =>
  mode.value === "continue"
    ? limit.value?.notify_amount_usd ?? props.notificationAmount
    : null
);

// Precio por API call listo para copy; sin `overage_price` se usa una frase neutra.
// El resto del texto vive en el template porque lleva partes en negrita (`<strong>`).
const pricePhrase = computed(() => priceText.value || "su costo individual");

// Cambio diferido: hay `pending_amount_usd` que regirá desde `pending_from_period`.
const hasPendingLimitChange = computed(
  () => limit.value?.pending_amount_usd != null && limit.value?.pending_from_period != null
);

// El indicador `Límite de consumo: $X de $Y` solo existe cuando hay un monto configurado y su
// consumo es resoluble. Sin `amount_usd` el diseño no muestra el bloque: renderizarlo dejaría la
// frase truncada ("Límite de consumo: $0 de ") en la vista y en el árbol de accesibilidad. El
// `v-if` del bloque cubre también su tooltip, que vive dentro del mismo `<p>`.
const showLimitIndicator = computed(
  () => mode.value === "stop" && limitAmount.value !== null && consumedAmount.value !== null
);

const showLimitModal = ref(false);

const handleSaveLimit = () => {
  // El modal ya persistió con `PUT /api/v1/api-cloud/limit/`; se relee el endpoint para que la
  // barra tome el monto en vigor (el previo si el cambio quedó diferido) y el consumo actualizado.
  fetchLimit();
};

const goToConsumption = () => {
  router.push({ path: "/billing", query: { key: "recharges", tab: "api-calls-wallet" } });
};

onMounted(async () => {
  await Promise.all([fetchUsages(), fetchLimit()]);
  // El respaldo por slots solo se consulta si el campo propio de API Cloud está ausente.
  if (apiPrePayment.value === null) loadBillingTypeFallback();
});
</script>

<style scoped lang="sass">
.usage
  display: flex
  align-items: center
  justify-content: space-between
  gap: 24px
  margin-top: 24px
  padding: 12px 16px
  border: 1px solid $gray-5
  border-radius: 8px

  &__main
    flex: 1
    min-width: 0
    display: flex
    flex-direction: column
    gap: 4px

  &__top
    display: flex
    align-items: flex-start
    justify-content: space-between
    gap: 16px

  &__title
    margin-bottom: 4px
    display: flex
    align-items: center
    gap: 6px

  &__used
    font-size: 16px
    font-weight: 600
    color: $gray-10

  &__divider,
  &__limit
    font-size: 14px
    font-weight: 400
    color: $gray-7

  &__caption-inline
    font-size: 14px
    font-weight: 400
    color: $gray-7

  &__side
    display: flex
    flex-direction: column
    align-items: flex-end
    gap: 4px
    text-align: right

  &__amount
    margin: 0
    display: flex
    align-items: center
    gap: 6px
    font-size: 14px
    color: $gray-9

  &__info
    color: $gray-7

  &__popover
    max-width: 268px

    p
      margin: 0 0 6px
      font-size: 14px
      line-height: 1.45

    strong
      font-weight: 600

  &__popover-link
    padding: 0
    height: auto
    font-size: 14px

  &__caption
    margin: 0
    font-size: 12px
    color: $gray-7

  &__message
    margin: 4px 0 0
    font-size: 12px
    color: $gray-7

  &__pending
    margin-top: 8px

  &__icon
    margin-right: 6px

  &--low &__message
    color: #d48806

  &--exhausted &__message
    color: #cf1322

  &__notify
    color: $gray-7

  &__actions
    display: flex
    flex-shrink: 0
    gap: 8px
</style>
